import { type RpcLimitOrder } from '@chainflip/rpc/parsers';
import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import { tickToRate } from '@chainflip/utils/tickMath';
import { useMutation } from '@tanstack/react-query';
import { ReadonlyButton } from '@/shared/components/dashboards/Readonly';
import { getPercentageFilled } from '@/shared/components/orders/OrderStatus';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useToast from '@/shared/hooks/useToast';
import useTracking from '@/shared/hooks/useTracking';
import { chainflipAssetMap, TokenAmount } from '@/shared/utils';
import { useStateChainAccount } from '../../hooks/useStateChainAccount';
import { LiquidityProvisionEvents, type LiquidityProvisionTrackEvents } from '../../types/track';

export const CloseLimitOrderButton = ({
  order,
  baseAsset,
}: {
  order: RpcLimitOrder;
  baseAsset: BaseChainflipAsset;
}) => {
  const { sendExtrinsic } = useStateChainAccount();
  const track = useTracking<LiquidityProvisionTrackEvents>();
  const { selectedAccount: polkadotAccount } = usePolkadot();
  const toast = useToast('close-limit-order-button');
  const limitPrice = tickToRate(order.tick, baseAsset);

  const sellAmount = TokenAmount.fromAsset(
    order.original_sell_amount,
    order.type === 'bid' ? 'Usdc' : baseAsset,
  );
  const earnedFees = TokenAmount.fromAsset(
    order.fees_earned,
    order.type === 'ask' ? 'Usdc' : baseAsset,
  );
  const fillPercentage = getPercentageFilled(order.sell_amount, order.original_sell_amount);

  const { mutate: close, isPending } = useMutation({
    mutationFn: async () => {
      toast.loading('Submitting transaction to the State Chain...');
      track(LiquidityProvisionEvents.CloseLimitOrder, {
        props: {
          path: window.location.pathname,
          scAccount: polkadotAccount?.idSs58 ?? '-',
          position: 'open_orders',
          type: 'limit',
          side: order.type === 'bid' ? 'Buy' : 'Sell',
          price: limitPrice.toFixed(),
          amount: sellAmount.toFixed(),
          collectableFees: earnedFees.toFixed(),
          fillPercentage,
        },
      });

      return sendExtrinsic('set-limit-order', {
        baseAsset,
        quoteAsset: 'Usdc',
        side: order.type === 'bid' ? 'Buy' : 'Sell',
        orderId: BigInt(order.id),
        tick: order.tick,
        sellAmount: TokenAmount.fromAsset(0, baseAsset),
      });
    },
    onSuccess() {
      track(LiquidityProvisionEvents.CloseLimitOrderSuccess, {
        props: {
          path: window.location.pathname,
          scAccount: polkadotAccount?.idSs58 ?? '-',
          position: 'open_orders',
          type: 'limit',
          side: order.type === 'bid' ? 'Buy' : 'Sell',
          price: limitPrice.toFixed(),
          amount: sellAmount.toFixed(),
          collectableFees: earnedFees.toFixed(),
          fillPercentage,
        },
      });

      if (order.type === 'bid') {
        toast.success(
          `Limit order to buy ${sellAmount.toFormatted()} ${
            chainflipAssetMap.Usdc.symbol
          } worth of ${chainflipAssetMap[baseAsset].symbol} @ ${limitPrice.toFixed(2)} USDC closed`,
        );
      } else {
        toast.success(
          `Limit order to sell ${sellAmount.toFormatted()} ${
            chainflipAssetMap[baseAsset].symbol
          } @ ${limitPrice.toFixed(2)} ${chainflipAssetMap.Usdc.symbol} closed`,
        );
      }
    },
    onError(err) {
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });

  return (
    <ReadonlyButton
      type="secondary-standard"
      size="small"
      loading={isPending && 'iconOnly'}
      onClick={(e) => {
        e.stopPropagation();
        close();
      }}
    >
      Close
    </ReadonlyButton>
  );
};
